import { FunctionComponent } from 'react'
import styled, { css } from 'styled-components'
import contextMegaModule, { MegaModuleContextType } from '@contexts/megaModule'
import { desktopCSS, tabletCSS, mobileCSS } from '@measures/responsive'
import useRenderer from '@hooks/useRenderer'
import { MegaModuleContent } from '@widgets/Multiline/types'

const { MegaModuleContext } = contextMegaModule

interface MegaModuleMultilineProps {
  children: MegaModuleContent
}

interface StyledLineProps {
  color: MegaModuleContextType['accentColor']
}

interface StyledAdditionalTeasersProps {
  itemsNumber: MegaModuleContextType['size']
}

const StyledLine = styled.div<StyledLineProps>`
  ${({ color }) => {
    return css`
      position: absolute;
      top: 0;
      left: 0;
      height: calc(100% - 20px);
      width: 8px;
      background-color: ${color};
    `
  }}
`

const StyledMegaModuleMultiline = styled.div`
  ${({
    theme: {
      colors: {
        grey: { grey_20 },
      },
    },
  }) => css`
    background-color: ${grey_20};
    width: 100%;
    grid-column: span 6;
  `}
`

const StyledAdditionalTeasers = styled.div<StyledAdditionalTeasersProps>`
  ${({ itemsNumber }) => {
    if (itemsNumber < 3) {
      return css`
        display: none;
      `
    }

    return css`
      position: relative;
      padding-top: 20px;
      padding-left: 8px;
      padding-bottom: 20px;
      box-sizing: border-box;
      width: 100%;
      display: grid;

      ${desktopCSS(css`
        grid-gap: 20px;
        grid-template-columns: repeat(
          ${itemsNumber - 1 === 3 ? 3 : 2},
          minmax(0, 1fr)
        );
      `)}

      ${tabletCSS(css`
        grid-gap: 20px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        ${itemsNumber === 4 &&
        css`
          > *:nth-child(2) {
            grid-column: span 2;
            min-width: 0;
          }
        `}
      `)}

      ${mobileCSS(css`
        grid-gap: 10px;
        grid-template-columns: repeat(1, minmax(0, 1fr));
      `)}
    `
  }}
`

const MegaModuleMultiline: FunctionComponent<MegaModuleMultilineProps> = ({
  children,
}) => {
  const Renderer = useRenderer()

  if (children.length < 1) {
    return null
  }

  const [megaModuleElement, ...restOfElements] = children

  const { size, accentColor, mainTeaser, additionalTeasers } = megaModuleElement

  //* Return only the main teaser when mega module is not populated enough.
  //* We need at least the Main Teaser and two additional Teaser to be present.
  if (size < 3 || !additionalTeasers) {
    return (
      <>
        <Renderer>{mainTeaser}</Renderer>
        <Renderer>{restOfElements}</Renderer>
      </>
    )
  }

  const mainTeaserWithoutLayoutItems = {
    ...mainTeaser.content[0],
    parentProps: {
      flex: '6',
    },
  }

  const additionalTeasersWithoutLayoutItems = additionalTeasers.map(
    (layoutItem, index) => ({
      ...layoutItem.content[0],
      parentProps: { positionInMegaModule: index + 1 },
    })
  )

  return (
    <>
      <MegaModuleContext.Provider value={{ accentColor, size }}>
        <StyledMegaModuleMultiline>
          <Renderer>{mainTeaserWithoutLayoutItems}</Renderer>
          <StyledAdditionalTeasers itemsNumber={size}>
            <StyledLine color={accentColor} />
            <Renderer>{additionalTeasersWithoutLayoutItems}</Renderer>
          </StyledAdditionalTeasers>
        </StyledMegaModuleMultiline>
      </MegaModuleContext.Provider>
      <Renderer>{restOfElements}</Renderer>
    </>
  )
}

export default MegaModuleMultiline
